import { Fragment, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { useAuditLog } from '@/api/hooks';
import { EmptyState, ErrorState, PageHeader } from '@/components/states';
import { formatDateTime } from '@/features/license-assignment/cellState';
import type { AuditEntry } from '@/types/api';
import { ACTIONS, describe, fieldChanges } from './describe';

const PAGE_SIZE = 25;
const COLUMNS = 5;

/** The scheduled import, by what it is rather than by its technical account. */
function actorLabel(actor: string): string {
  return actor === 'system:cronjob' ? 'Nachtlauf' : actor;
}

interface EntryDetailsProps {
  entry: AuditEntry;
}

/**
 * Everything the summary line leaves out: every field with its old and new
 * value, the changed ones marked, and the raw documents for whoever needs to
 * see exactly what was stored.
 */
function EntryDetails({ entry }: EntryDetailsProps) {
  const [raw, setRaw] = useState(false);
  const changes = fieldChanges(entry.beforeJson, entry.afterJson);
  const hasBefore = entry.beforeJson !== null;

  return (
    <Stack spacing={1.5} sx={{ py: 1.5 }}>
      {changes.length > 0 && (
        <Table size="small" sx={{ maxWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>Feld</TableCell>
              {hasBefore && <TableCell>Vorher</TableCell>}
              <TableCell>{hasBefore ? 'Nachher' : 'Wert'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changes.map((change) => (
              <TableRow key={change.key}>
                <TableCell sx={{ color: 'text.secondary' }}>{change.label}</TableCell>
                {hasBefore && (
                  <TableCell sx={{ wordBreak: 'break-word' }}>{change.before}</TableCell>
                )}
                <TableCell
                  sx={{ wordBreak: 'break-word', fontWeight: change.changed ? 600 : undefined }}
                >
                  {change.after}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          {entry.action} · {entry.entityType}
          {entry.entityId !== null && ` #${entry.entityId}`}
          {entry.requestId && ` · Request ${entry.requestId}`}
        </Typography>
        <Button size="small" onClick={() => setRaw((open) => !open)} sx={{ textTransform: 'none' }}>
          {raw ? 'Rohdaten ausblenden' : 'Rohdaten anzeigen'}
        </Button>
      </Stack>
      {raw && (
        <Box
          component="pre"
          sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', fontSize: 12 }}
        >
          {JSON.stringify({ before: entry.beforeJson, after: entry.afterJson }, null, 2)}
        </Box>
      )}
    </Stack>
  );
}

export function AuditPage() {
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [details, setDetails] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const filters = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(page * PAGE_SIZE),
  });
  if (actor) filters.set('actor', actor);
  if (action) filters.set('action', action);
  // Trimmed here as well as on the server: a trailing space from a paste would
  // otherwise change the query key and refetch for the same search.
  if (details.trim()) filters.set('details', details.trim());

  const { data, isPending, error, refetch } = useAuditLog(filters.toString());

  return (
    <Box>
      <PageHeader
        title="Protokoll"
        description="Jede Änderung an Lizenzen, Ausnahmen und Tokens — wer, was, wann."
        actions={
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            href={`/api/v1/audit/export.csv?${filters.toString()}`}
          >
            CSV exportieren
          </Button>
        }
      />

      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Benutzer"
          value={actor}
          onChange={(event) => {
            setActor(event.target.value);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', md: 260 } }}
        />
        <TextField
          select
          label="Aktion"
          value={action}
          onChange={(event) => {
            setAction(event.target.value);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', md: 260 } }}
        >
          <MenuItem value="">Alle</MenuItem>
          {Object.entries(ACTIONS).map(([key, info]) => (
            <MenuItem key={key} value={key}>
              {info.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Details"
          placeholder="z. B. aew"
          value={details}
          onChange={(event) => {
            setDetails(event.target.value);
            setPage(0);
          }}
          helperText="Sucht in den Details, auch im Zustand davor, und im Namen des betroffenen Benutzers oder Mandanten."
          sx={{ flex: 1, minWidth: { md: 260 } }}
        />
      </Stack>

      {isPending ? (
        <Skeleton variant="rectangular" height={320} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Keine Einträge" description="Für diese Filter gibt es kein Protokoll." />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 150 }}>Zeitpunkt</TableCell>
                <TableCell sx={{ width: 230 }}>Benutzer</TableCell>
                <TableCell sx={{ width: 190 }}>Aktion</TableCell>
                <TableCell>Details</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((entry) => {
                const description = describe(entry);
                const open = expanded === entry.id;
                const toggle = () => setExpanded(open ? null : entry.id);
                return (
                  <Fragment key={entry.id}>
                    <TableRow
                      hover
                      onClick={toggle}
                      sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(entry.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={entry.actor === actorLabel(entry.actor) ? '' : entry.actor}>
                          <span>{actorLabel(entry.actor)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={description.label}
                          color={description.tone}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={description.problem ? 'error' : undefined}
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {description.subject && <strong>{description.subject}</strong>}
                          {description.subject && description.text && ': '}
                          {description.text}
                        </Typography>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          aria-label={open ? 'Details ausblenden' : 'Details anzeigen'}
                          aria-expanded={open}
                          onClick={(event) => {
                            // The row toggles too; without this it toggles twice.
                            event.stopPropagation();
                            toggle();
                          }}
                        >
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={COLUMNS} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} unmountOnExit>
                          <EntryDetails entry={entry} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_event, next) => {
              setPage(next);
              setExpanded(null);
            }}
          />
        </TableContainer>
      )}
    </Box>
  );
}
